import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import Panel from "../../components/brand/Panel";
import DataTable from "../../components/brand/DataTable";
import { getFiscalYears, getYearEndPack } from "../../api/financeApi";
import { getApiErrorMessage } from "../../utils/concurrency";
import { formatDateUk } from "../../utils/dateDisplay";

const PAGE_SIZE = 10;

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

const blankIfZero = (value) => (value ? money(value) : "");

function Pager({ data, onChange }) {
  if (!data || (data.total_pages || 1) <= 1) return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
      <Pagination count={data.total_pages} page={data.page} onChange={(_, value) => onChange(value)} />
    </Box>
  );
}

export default function YearEndPackPanel({ propertyId, onError }) {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [fiscalYearId, setFiscalYearId] = useState("");
  const [tbPage, setTbPage] = useState(1);
  const [summaryPage, setSummaryPage] = useState(1);
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getFiscalYears(propertyId)
      .then((res) => {
        if (cancelled) return;
        const years = res.data.results || [];
        setFiscalYears(years);
        const current = years.find((year) => year.is_current) || years[0];
        setFiscalYearId(current ? String(current.id) : "");
        if (!current) setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        onError?.(getApiErrorMessage(e, "Failed to load fiscal years."));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, onError]);

  useEffect(() => {
    if (!fiscalYearId) return undefined;
    let cancelled = false;
    setLoading(true);
    onError?.("");
    getYearEndPack(propertyId, {
      fiscal_year_id: fiscalYearId,
      tb_page: tbPage,
      summary_page: summaryPage,
      page_size: PAGE_SIZE,
    })
      .then((res) => {
        if (!cancelled) setPack(res.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setPack(null);
        onError?.(getApiErrorMessage(e, "Failed to load the year-end pack."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, fiscalYearId, tbPage, summaryPage, onError]);

  const changeFiscalYear = (value) => {
    setFiscalYearId(value);
    setTbPage(1);
    setSummaryPage(1);
  };

  const trialBalance = pack?.trial_balance;
  const summary = pack?.service_charge_summary;
  const yearEnd = pack?.fiscal_year?.end_date;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {pack?.notice && <Alert severity="info">{pack.notice}</Alert>}

      {fiscalYears.length === 0 && !loading ? (
        <Alert severity="warning">Add a fiscal year before producing the year-end pack.</Alert>
      ) : (
        <FormControl size="small" sx={{ maxWidth: 320 }}>
          <InputLabel>Fiscal year</InputLabel>
          <Select
            label="Fiscal year"
            value={fiscalYearId}
            onChange={(e) => changeFiscalYear(e.target.value)}
          >
            {fiscalYears.map((year) => (
              <MenuItem key={year.id} value={String(year.id)}>
                {year.label}
                {year.is_current ? " (current)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {loading && !pack ? (
        <Skeleton height={240} />
      ) : (
        pack && (
          <>
            <Panel
              eyebrow={yearEnd ? `Year ending ${formatDateUk(yearEnd)}` : undefined}
              title="Trial balance"
              right={
                <Chip
                  size="small"
                  color={trialBalance.balanced ? "success" : "error"}
                  label={trialBalance.balanced ? "Balanced" : "Does not balance"}
                />
              }
            >
              {trialBalance.total === 0 ? (
                <Box sx={{ p: 2.75 }}>
                  <Typography color="text.secondary">
                    No ledger entries up to the end of this fiscal year.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <DataTable size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Account</TableCell>
                        <TableCell align="right">Opening</TableCell>
                        <TableCell align="right">Debits in year</TableCell>
                        <TableCell align="right">Credits in year</TableCell>
                        <TableCell align="right">Closing debit</TableCell>
                        <TableCell align="right">Closing credit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trialBalance.results.map((row) => (
                        <TableRow key={row.account_id}>
                          <TableCell>{row.account_code}</TableCell>
                          <TableCell>{row.account_name}</TableCell>
                          <TableCell align="right">
                            {row.opening_debit
                              ? `${money(row.opening_debit)} Dr`
                              : row.opening_credit
                                ? `${money(row.opening_credit)} Cr`
                                : ""}
                          </TableCell>
                          <TableCell align="right">{blankIfZero(row.period_debit)}</TableCell>
                          <TableCell align="right">{blankIfZero(row.period_credit)}</TableCell>
                          <TableCell align="right">{blankIfZero(row.closing_debit)}</TableCell>
                          <TableCell align="right">{blankIfZero(row.closing_credit)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                        <TableCell colSpan={2}>Total, all accounts</TableCell>
                        <TableCell />
                        <TableCell align="right">{money(trialBalance.totals.period_debit)}</TableCell>
                        <TableCell align="right">{money(trialBalance.totals.period_credit)}</TableCell>
                        <TableCell align="right">{money(trialBalance.totals.closing_debit)}</TableCell>
                        <TableCell align="right">{money(trialBalance.totals.closing_credit)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </DataTable>
                </TableContainer>
              )}
              <Box sx={{ pb: 2 }}>
                <Pager data={trialBalance} onChange={setTbPage} />
              </Box>
            </Panel>

            <Panel
              eyebrow={pack.fiscal_year.label}
              title="Service charge account summary"
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 2,
                  p: 2.75,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">Income</Typography>
                  <Typography fontWeight={700}>{money(summary.totals.income)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Expenditure</Typography>
                  <Typography fontWeight={700}>{money(summary.totals.expenditure)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {summary.totals.surplus < 0 ? "Deficit" : "Surplus"}
                  </Typography>
                  <Typography fontWeight={700}>{money(Math.abs(summary.totals.surplus))}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Reserve movements (not in surplus)
                  </Typography>
                  <Typography fontWeight={700}>{money(summary.totals.reserve_movements)}</Typography>
                </Box>
              </Box>
              {summary.total === 0 ? (
                <Box sx={{ px: 2.75, pb: 2.75 }}>
                  <Typography color="text.secondary">
                    No income, expenditure or reserve movements in this fiscal year.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <DataTable size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Section</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Account</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.results.map((row) => (
                        <TableRow key={`${row.section}-${row.account_id}`}>
                          <TableCell>{row.section_label}</TableCell>
                          <TableCell>{row.account_code}</TableCell>
                          <TableCell>{row.account_name}</TableCell>
                          <TableCell align="right">{money(row.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </DataTable>
                </TableContainer>
              )}
              <Box sx={{ pb: 2 }}>
                <Pager data={summary} onChange={setSummaryPage} />
              </Box>
            </Panel>
          </>
        )
      )}
    </Box>
  );
}
